import React from "react";
import { Button } from "@heroui/button";
import { Folder, FolderOpen, Eye, EyeOff, Trash2 } from "lucide-react";

import { Category } from "../../types/menu";

interface CategoriesTableProps {
  categories: Category[];
  loading: boolean;
  onEdit: (category: Category) => void;
  onDelete: (categoryId: number) => void;
  onToggleVisibility: (category: Category) => void;
}

const CategoriesTable: React.FC<CategoriesTableProps> = ({
  categories,
  loading,
  onEdit,
  onDelete,
  onToggleVisibility,
}) => {
  // Confirm deletion
  const confirmDelete = (category: Category) => {
    // Check if there are products in this category first
    // This would be implemented in a real app
    if (
      window.confirm(
        `Are you sure you want to delete "${category.name}"? This action cannot be undone.`,
      )
    ) {
      onDelete(category.id);
    }
  };

  // Loading state
  if (loading && categories.length === 0) {
    return (
      <div className="p-8 text-center">
        <FolderOpen className="inline-block w-8 h-8 animate-spin mb-4" />
        <p className="text-gray-500 dark:text-gray-400">
          Loading categories...
        </p>
      </div>
    );
  }

  // Empty state
  if (!loading && categories.length === 0) {
    return (
      <div className="p-8 text-center">
        <Folder className="h-16 w-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" />
        <p className="text-xl font-semibold text-gray-600 dark:text-gray-300 mb-2">
          No categories found
        </p>
        <p className="text-gray-500 dark:text-gray-400 mb-6">
          Create your first menu category to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              ID
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Name
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Description
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Position
            </th>
            <th
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Visibility
            </th>
            <th
              className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              scope="col"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
          {categories.map((category) => (
            <tr
              key={category.id}
              className={
                category.hidden ? "bg-gray-50 dark:bg-gray-900/30" : ""
              }
            >
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                {category.id}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {category.name}
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {category.description || "No description"}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                {category.position !== null ? category.position : "Not set"}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    category.hidden
                      ? "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300"
                      : "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300"
                  }`}
                >
                  {category.hidden ? "Hidden" : "Visible"}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                <Button
                  color="primary"
                  size="sm"
                  variant="light"
                  onPress={() => onEdit(category)}
                >
                  <Eye className="w-4 h-4 mr-2" /> Edit
                </Button>
                <Button
                  color={category.hidden ? "success" : "warning"}
                  size="sm"
                  variant="light"
                  onPress={() => onToggleVisibility(category)}
                >
                  {category.hidden ? (
                    <Eye className="w-4 h-4 mr-2" />
                  ) : (
                    <EyeOff className="w-4 h-4 mr-2" />
                  )}
                  {category.hidden ? "Show" : "Hide"}
                </Button>
                <Button
                  color="danger"
                  size="sm"
                  variant="light"
                  onPress={() => confirmDelete(category)}
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CategoriesTable;
